import { queryOptions } from '@tanstack/vue-query'
import type { GroupedProducts, MealRecommendationsResult } from '@repo/api-types'
import { useClient } from '@/composables/useClient'
import { entityKeys } from '@/queries/entity-keys'
import { HttpError } from '@/queries/auth-error'

const productKeys = {
  list: () => [...entityKeys.product()] as const,
  recommendations: (animalId: string) => [...entityKeys.product(), 'recommendations', animalId] as const,
}

export const productQueries = {
  /**
   * Full catalog of meals, treats, supplements, collaterals (raw — visibility/sort are
   * applied in `useProducts()` via `computed` derivations to preserve the
   * exact shape of the old `useProductStore` exports).
   */
  list: () =>
    queryOptions({
      queryKey: productKeys.list(),
      queryFn: async (): Promise<GroupedProducts> => {
        const client = await useClient()
        const { status, body } = await client.product.getProducts()

        if (status === 200) return body

        throw new HttpError(status, body, 'Could not fetch products')
      },
    }),

  /**
   * Per-animal meal recommendations. Replaces the imperative
   * `useProducts().getMealRecommendations(animalId)` from the old composable.
   */
  recommendations: (animalId: string) =>
    queryOptions({
      queryKey: productKeys.recommendations(animalId),
      queryFn: async (): Promise<MealRecommendationsResult> => {
        const client = await useClient()
        const { status, body } = await client.product.getMealRecommendations({ params: { animalId } })

        if (status === 200) return body

        throw new HttpError(status, body, 'Could not fetch meal recommendations')
      },
    }),
}
